import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { AppShell } from "../../components/Sidebar";
import { StatusBadge, LegendDots } from "../../components/StatusBadge";
import { api } from "../../lib/api";
import { ArrowLeft, ChevronRight, BookOpen, Clock, Star, Info, FileSpreadsheet, FileText } from "lucide-react";
import { API } from "../../lib/api";

const downloadExport = (studentId, format) => {
  const token = localStorage.getItem("gd_token");
  fetch(`${API}/teacher/export/student/${studentId}?format=${format}`, { headers: { Authorization: `Bearer ${token}` } })
    .then(async (r) => {
      const blob = await r.blob();
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m?.[1] || `student.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    });
};

const initials = (name) => name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/teacher/student/${id}`).then((r) => setData(r.data)).catch(() => navigate("/teacher/dashboard"));
  }, [id, navigate]);

  if (!data) return <AppShell role="teacher"><p className="text-slate-500">Loading…</p></AppShell>;
  const { student, summary, module_performance } = data;

  return (
    <AppShell role="teacher">
      <div className="flex items-start justify-between">
        <div>
          <nav className="flex items-center gap-1.5 text-sm text-slate-500">
            <Link to="/teacher/dashboard" className="hover:text-slate-700">Dashboard</Link>
            <ChevronRight size={14} />
            <Link to="/teacher/dashboard" className="hover:text-slate-700">Students</Link>
            <ChevronRight size={14} />
            <span className="font-semibold text-slate-900">{student.full_name}</span>
          </nav>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-slate-900">Student Detail</h1>
          <p className="mt-1 text-slate-500">Detailed progress and diagnostic insights for {student.full_name}.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => downloadExport(id, "xlsx")} data-testid="export-student-xlsx" className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100">
            <FileSpreadsheet size={14} /> Excel
          </button>
          <button onClick={() => downloadExport(id, "pdf")} data-testid="export-student-pdf" className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-700 hover:bg-rose-100">
            <FileText size={14} /> PDF
          </button>
          <button onClick={() => navigate("/teacher/dashboard")} data-testid="back-to-students" className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <ArrowLeft size={16} /> Back
          </button>
        </div>
      </div>

      {/* Profile + summary cards */}
      <div className="mt-7 grid gap-5 lg:grid-cols-[2fr_1fr_1fr_1fr]">
        <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
          <div className="flex items-start gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-indigo-100 text-2xl font-bold text-indigo-700">
              {initials(student.full_name)}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-900">{student.full_name}</h2>
              <p className="mt-1 text-sm text-slate-500">Roll No. {student.roll_no} • {student.class_label} • {student.school_name}</p>
              <div className="mt-3 flex items-center gap-3">
                <StatusBadge value="Active" withDot />
                <span className="text-xs text-slate-500">Last active: {student.last_active}</span>
              </div>
            </div>
          </div>
        </div>

        <SummaryCard icon={BookOpen} color="bg-violet-100 text-violet-600" label="Modules Completed" value={`${summary.modules_completed} / ${summary.total_modules}`} sub={`${Math.round(summary.modules_completed / summary.total_modules * 100)}% Completed`} progress={Math.round(summary.modules_completed / summary.total_modules * 100)} />
        <SummaryCard icon={Clock} color="bg-emerald-100 text-emerald-600" label="Total Time Spent" value={summary.total_time_spent} sub="Across all modules" />
        <SummaryCard icon={Star} color="bg-amber-100 text-amber-600" label="Average Quiz Score" value={`${summary.average_quiz_score}%`} sub="Across all modules" />
      </div>

      {/* Module-wise table */}
      <div className="mt-7 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-bold text-slate-900">Module-wise Performance &amp; Diagnostic Insights</h3>
          <LegendDots />
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th rowSpan={2} className="py-3 pr-4 align-bottom">#</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">Chapter Name</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">Status</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">Time Spent</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">Questions Attempted</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">Quiz Score</th>
                <th colSpan={3} className="py-3 pr-4 text-center font-semibold text-slate-700">Diagnostics</th>
              </tr>
              <tr className="border-b border-slate-100 text-xs font-semibold text-slate-500">
                <th className="py-2 pr-4">Conceptual Understanding</th>
                <th className="py-2 pr-4">Applied Learning</th>
                <th className="py-2 pr-4">Theoretical Learning</th>
              </tr>
            </thead>
            <tbody>
              {module_performance.map((p, i) => (
                <tr key={p.module_id} className="border-b border-slate-50 transition-colors hover:bg-slate-50/60" data-testid={`module-row-${p.module_id}`}>
                  <td className="py-4 pr-4 text-slate-500">{i + 1}</td>
                  <td className="py-4 pr-4 font-medium text-slate-900">{p.module_name}</td>
                  <td className="py-4 pr-4"><StatusBadge value={p.status} /></td>
                  <td className="py-4 pr-4 text-slate-600">{p.time_spent}</td>
                  <td className="py-4 pr-4 text-slate-700" data-testid={`questions-attempted-${p.module_id}`}>
                    {p.questions_attempted || 0}
                  </td>
                  <td className="py-4 pr-4 font-semibold text-slate-700">{p.quiz_score != null ? `${p.quiz_score}%` : "—"}</td>
                  <td className="py-4 pr-4"><StatusBadge value={p.conceptual} /></td>
                  <td className="py-4 pr-4"><StatusBadge value={p.applied} /></td>
                  <td className="py-4 pr-4"><StatusBadge value={p.theoretical} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center gap-2 rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-500">
          <Info size={14} /> Diagnostic insights are generated based on performance in conceptual questions, application-based questions and interactive VR activities.
        </div>
      </div>
    </AppShell>
  );
}

const SummaryCard = ({ icon: Icon, color, label, value, sub, progress }) => (
  <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card">
    <div className="flex items-start gap-3">
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${color}`}><Icon size={20} /></div>
      <div className="flex-1">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className="mt-1 text-xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{sub}</p>
        {progress != null && (
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full bg-indigo-600" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
    </div>
  </div>
);
